import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Typography,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  Box,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import EngineeringIcon from "@mui/icons-material/Engineering";
import GroupsIcon from "@mui/icons-material/Groups";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import PersonIcon from "@mui/icons-material/Person";
import baseApi from "../api/baseApi";
import { AccountTree } from "@mui/icons-material";
import { useTranslations, useOrganizations, useSelectedOrganization } from "hooks";
import { getTranslation } from "common";
import NotificationGroup from "./NotificationGroup";
import { Organization } from "model/organizationModel";

export interface NotificationConfig {
  id: number;
  iddescribeusersettings: string;
  active: boolean;
  name: string;
}

export const CATEGORY_TO_SECTION_ID: Record<string, number> = {
  "NOTIFICATION.EDIT.GROUPS": 5,
  "NOTIFICATION.EDIT.ROLES": 3,
  "NOTIFICATION.EDIT.USERS": 4,
  "NOTIFICATION.EDIT.ORGANIZATION": 2,
  "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU": 1,
};

const NotificationsSettings: React.FC = () => {
  const CATEGORY_MAP: Record<string, { label: string; icon: JSX.Element }> = {
    "NOTIFICATION.EDIT.GROUPS": { label: 'generic_group_notifications', icon: <GroupsIcon sx={{ color: "primary.main" }} /> },
    "NOTIFICATION.EDIT.ROLES": { label: "generic_notification.roles", icon: <EngineeringIcon sx={{ color: "primary.main" }} /> },
    "NOTIFICATION.EDIT.USERS": { label: "generic_notification.users", icon: <PersonIcon sx={{ color: "primary.main" }} /> },
    "NOTIFICATION.EDIT.ORGANIZATION": { label: "generic_notification.organization", icon: <GroupWorkIcon sx={{ color: "primary.main" }} /> },
    "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU": { label: "generic_notification.activities", icon: <AccountTree sx={{ color: "primary.main" }} /> }
  };

  const CHANNEL_IDS: Record<string, number> = {
    "Push": 1,
    "Email": 2,
  };

  const [notificationsConfig, setNotificationsConfig] = useState<NotificationConfig[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [allOrganizations, setAllOrganizations] = useState(false);
  const [selectedNotificationOrganizationId, setSelectedNotificationOrganizationId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [organizations] = useOrganizations();
  const selectedOrganization = useSelectedOrganization();
  const translations = useTranslations()

  useEffect(() => {
    if (selectedOrganization?.id) {
      setSelectedNotificationOrganizationId(selectedOrganization.id);
    }
  }, [selectedOrganization]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const url = allOrganizations || !selectedNotificationOrganizationId
          ? "/notifications/configuration"
          : `/notifications/configuration/${selectedNotificationOrganizationId}`;
        const response = await baseApi.get(url);
        setNotificationsConfig(response.data);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedNotificationOrganizationId, allOrganizations]);

  const handleActiveToggle = async (categoryPrefix: string, type: string, isActive: boolean) => {
    const idsection = CATEGORY_TO_SECTION_ID[categoryPrefix];
    const idchannel = CHANNEL_IDS[type];

    if (!idsection || !idchannel) {
      setError("Invalid category or channel");
      return;
    }

    try {
      const payload: Record<string, unknown> = {
        idsection,
        idchannel,
        active: !isActive,
        all_organizations: allOrganizations,
      };

      if (!allOrganizations && selectedNotificationOrganizationId) {
        payload.idorganization = selectedNotificationOrganizationId;
      }

      await baseApi.post("notifications/configuration/section", payload);

      setNotificationsConfig(prev =>
        prev.map(notification => {
          if (
            notification.iddescribeusersettings.startsWith(categoryPrefix) &&
            notification.name === type
          ) {
            return { ...notification, active: !isActive };
          }
          return notification;
        })
      );
    } catch (err) {
    }
  };

  const groupedNotifications: Record<string, NotificationConfig[]> = useMemo(() => {

    const _groupedNotifications: Record<string, NotificationConfig[]> = {};
    notificationsConfig?.forEach(notification => {
      const matchingPrefix = Object.keys(CATEGORY_MAP).find(prefix =>
        notification.iddescribeusersettings.startsWith(prefix)
      );
      if (matchingPrefix) {
        const categoryLabel = CATEGORY_MAP[matchingPrefix].label;
        if (!_groupedNotifications[categoryLabel]) {
          _groupedNotifications[categoryLabel] = [];
        }
        _groupedNotifications[categoryLabel].push(notification);
      }
    })

    return _groupedNotifications
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsConfig]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !(prev[category] ?? true),
    }));
  };

  return (
    <React.Fragment>
      <Typography variant="h4" sx={{ mb: 4, mt: 8, ml: 4, textAlign: "center" }}>
        {getTranslation(translations, 'generic.notifications.settings')}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3, ml: 4, mb: 2 }}>
        {!allOrganizations && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">
              {getTranslation(translations, "generic.select_organization")}:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={selectedNotificationOrganizationId ?? ""}
                onChange={(e) => setSelectedNotificationOrganizationId(e.target.value as number)}
                displayEmpty
              >
                {organizations.map((org: Organization) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        <FormControlLabel
          control={
            <Switch
              checked={allOrganizations}
              onChange={(e) => setAllOrganizations(e.target.checked)}
            />
          }
          label={getTranslation(translations, "generic.apply_to_all_organizations")}
        />
      </Box>
      <Grid container spacing={2} sx={{ width: "55%", ml: "auto", mr: "auto", mb: 5 }}>
        {Object.entries(groupedNotifications).map(([labelKey, notifications]) => {
          const categoryKey = Object.keys(CATEGORY_MAP).find(prefix =>
            notifications[0].iddescribeusersettings.startsWith(prefix)
          )
          return (
            <NotificationGroup
              key={`${labelKey}-${selectedNotificationOrganizationId ?? 'all'}-${allOrganizations}`}
              category={getTranslation(translations, labelKey)}
              categoryKey={categoryKey as string}
              expandedCategories={expandedCategories}
              handleActiveToggle={handleActiveToggle}
              notifications={notifications}
              toggleCategory={toggleCategory}
              categoryIcon={categoryKey ? CATEGORY_MAP[categoryKey].icon : ""}
            />
          );
        })}
      </Grid>
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default NotificationsSettings;
